import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { getApiBase } from "../apiBase";
import { isCvinExpansion } from "../projectGridColumns";
import {
  CONTRACT_STATUS_OPTIONS,
  canManageContracts,
  canViewContracts,
  contractStatusClass,
  contractStatusLabel,
  contractsPluginEnabled,
  formatContractCost,
  formatSignedContractCost,
} from "./contractsAccess";
import {
  contractExecutedFileUrl,
  contractTemplateDownloadUrl,
  contractWinningBidFileUrl,
  contractPermitFilesZipUrl,
  contractAdjustmentSupportFileUrl,
  createContract,
  createContractAdjustment,
  deleteContract,
  deleteContractAdjustment,
  fetchContract,
  fetchContractorOptions,
  fetchContracts,
  setContractProjects,
  updateContract,
  uploadExecutedContractFile,
  uploadWinningBidFile,
  uploadAdjustmentSupportFile,
} from "./contractsApi";
import {
  contractTableContractorLabel,
  formatContractSelectedFootage,
  formatProjectFootageLine,
  projectFootageTargets,
} from "./contractsProjectFootage";
import { formatFootageNumber } from "./ExpansionFootageProgressBar";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import { useBoardTheme } from "./boardThemeStore";
import "./contractsPage.css";

ModuleRegistry.registerModules([AllCommunityModule]);

function ContractTitleCell(props) {
  const id = props.data?.id;
  const title = props.value || "Untitled";
  if (!id) return title;
  return (
    <Link to={`/contracts/${id}`} className="contracts-link">
      {title}
    </Link>
  );
}

function ContractStatusCell(props) {
  const status = props.data?.status;
  return (
    <span className={`contracts-status ${contractStatusClass(status)}`}>
      {contractStatusLabel(status)}
    </span>
  );
}

function ContractFileDownloadCell(props) {
  const file = props.data?.[props.fileKey];
  const contractId = props.data?.id;
  const urlBuilder = props.context?.fileUrl;
  const getAuthHeaders = props.context?.getAuthHeaders;
  const label = props.label || "Download";

  if (!file?.id || !contractId || typeof urlBuilder !== "function") {
    return <span className="contracts-muted">—</span>;
  }

  const onDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await axios.get(urlBuilder(contractId), {
        headers: getAuthHeaders?.(),
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.filename || "contract-file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      /* ignore */
    }
  };

  return (
    <button
      type="button"
      className="contracts-grid-file-btn"
      title={`${label}: ${file.filename || "file"}`}
      aria-label={label}
      onClick={onDownload}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <polyline points="9 15 12 18 15 15" />
      </svg>
    </button>
  );
}

function ContractDropboxLinkCell(props) {
  const href = String(props.value || props.data?.dropboxLink || "").trim();
  if (!href) return <span className="contracts-muted">—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="contracts-link"
      title={href}
      onClick={(e) => e.stopPropagation()}
    >
      Open
    </a>
  );
}

function ContractPermitZipCell(props) {
  const contractId = props.data?.id;
  const projectCount = props.data?.projectIds?.length ?? props.data?.projects?.length ?? 0;
  const zipUrl = props.context?.permitZipUrl;
  const getAuthHeaders = props.context?.getAuthHeaders;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!contractId || typeof zipUrl !== "function" || !projectCount) {
    return <span className="contracts-muted">—</span>;
  }

  const onDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    setError("");
    try {
      const res = await axios.get(zipUrl(contractId), {
        headers: getAuthHeaders?.(),
        responseType: "blob",
      });
      const label = props.data?.contractNumberLabel || contractId;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `contract-${label}-permit-files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const data = err?.response?.data;
      let msg = "Could not download permit files.";
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
      } else if (typeof data?.error === "string") {
        msg = data.error;
      }
      setError(msg);
      window.alert(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="contracts-grid-file-btn"
      title={error || "Download permit plans and permits ZIP"}
      aria-label="Download permit files ZIP"
      disabled={busy}
      onClick={onDownload}
    >
      {busy ? (
        "…"
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      )}
    </button>
  );
}

function projectTypeLabel(row) {
  return isCvinExpansion(row) ? "Expansion" : "Commercial";
}

function toDateInputValue(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function projectLinkLabel(row) {
  const workOrder = row.work_order || row.workOrder || `#${row.id}`;
  const name = String(row.name || "").trim();
  return name ? `${workOrder} — ${name}` : workOrder;
}

function emptyContractDraft() {
  return {
    contractNumber: "",
    title: "",
    status: "pending_award",
    notes: "",
    contractor: "",
    fiberContractor: "",
    contractCost: "",
    contractExecutionDate: "",
    contractExpirationDate: "",
    projectLinks: [],
    dropboxLink: "",
  };
}

function normalizeCoveredFootageValue(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function projectLinksFromContract(contract) {
  if (!contract) return [];
  if (Array.isArray(contract.projectLinks) && contract.projectLinks.length) {
    return contract.projectLinks.map((link) => ({
      projectId: Number(link.projectId ?? link.id),
      appliesConstruction: link.appliesConstruction !== false,
      appliesFiber: link.appliesFiber !== false,
      coveredFootage: normalizeCoveredFootageValue(link.coveredFootage),
    }));
  }
  if (Array.isArray(contract.projects) && contract.projects.length) {
    return contract.projects.map((p) => ({
      projectId: Number(p.id),
      appliesConstruction: p.appliesConstruction !== false,
      appliesFiber: p.appliesFiber !== false,
      coveredFootage: normalizeCoveredFootageValue(p.coveredFootage),
    }));
  }
  return (contract.projectIds || []).map((id) => ({
    projectId: Number(id),
    appliesConstruction: true,
    appliesFiber: true,
    coveredFootage: null,
  }));
}

function contractToDraft(contract) {
  if (!contract) return emptyContractDraft();
  return {
    contractNumber:
      contract.contractNumberLabel != null ? String(contract.contractNumberLabel) : "",
    title: contract.title || "",
    status: contract.status || "pending_award",
    notes: contract.notes || "",
    contractor: contract.contractor || "",
    fiberContractor: contract.fiberContractor || "",
    contractCost: contract.contractCost != null ? String(contract.contractCost) : "",
    contractExecutionDate: toDateInputValue(contract.contractExecutionDate),
    contractExpirationDate: toDateInputValue(contract.contractExpirationDate),
    projectLinks: projectLinksFromContract(contract),
    dropboxLink: contract.dropboxLink || "",
  };
}

function roleLabelsForLink(link) {
  const parts = [];
  if (link?.appliesConstruction) parts.push("Construction");
  if (link?.appliesFiber) parts.push("Fiber");
  const covered = normalizeCoveredFootageValue(link?.coveredFootage);
  if (covered != null) parts.push(`${formatFootageNumber(covered)} ft`);
  return parts;
}

function defaultCoveragePlaceholder(row, link) {
  const t = projectFootageTargets(row);
  if (link?.appliesConstruction !== false && t.conduit != null && t.conduit > 0) {
    return formatFootageNumber(t.conduit);
  }
  if (link?.appliesFiber !== false && t.fiber != null && t.fiber > 0) {
    return formatFootageNumber(t.fiber);
  }
  if (t.conduit != null && t.conduit > 0) return formatFootageNumber(t.conduit);
  if (t.fiber != null && t.fiber > 0) return formatFootageNumber(t.fiber);
  return "Full";
}

function ProjectPicker({ projects, projectLinks, onChange, disabled }) {
  const [query, setQuery] = useState("");
  const linkById = useMemo(() => {
    const map = new Map();
    for (const link of projectLinks || []) {
      map.set(Number(link.projectId), link);
    }
    return map;
  }, [projectLinks]);
  const selected = useMemo(() => new Set(linkById.keys()), [linkById]);

  const selectedRows = useMemo(
    () => projects.filter((p) => selected.has(p.id)),
    [projects, selected]
  );
  const selectedFootage = useMemo(
    () => formatContractSelectedFootage(selectedRows, projectLinks),
    [selectedRows, projectLinks]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...projects].sort((a, b) =>
      String(a.work_order || "").localeCompare(String(b.work_order || ""), undefined, {
        numeric: true,
      })
    );
    if (!q) return list.slice(0, 200);
    return list
      .filter((row) => {
        const hay = `${row.work_order || ""} ${row.name || ""} ${row.customer || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 200);
  }, [projects, query]);

  const setLinks = (nextLinks) => {
    onChange(nextLinks);
  };

  const toggleProject = (id) => {
    if (disabled) return;
    const pid = Number(id);
    if (linkById.has(pid)) {
      setLinks((projectLinks || []).filter((link) => Number(link.projectId) !== pid));
      return;
    }
    setLinks([
      ...(projectLinks || []),
      {
        projectId: pid,
        appliesConstruction: true,
        appliesFiber: true,
        coveredFootage: null,
      },
    ]);
  };

  const setRole = (id, role, value) => {
    if (disabled) return;
    const pid = Number(id);
    const existing = linkById.get(pid);
    if (!existing) {
      setLinks(
        [
          ...(projectLinks || []),
          {
            projectId: pid,
            appliesConstruction: role === "construction" ? value : false,
            appliesFiber: role === "fiber" ? value : false,
            coveredFootage: null,
          },
        ].filter((link) => link.appliesConstruction || link.appliesFiber)
      );
      return;
    }
    const next = {
      ...existing,
      appliesConstruction: role === "construction" ? value : existing.appliesConstruction,
      appliesFiber: role === "fiber" ? value : existing.appliesFiber,
    };
    if (!next.appliesConstruction && !next.appliesFiber) {
      setLinks((projectLinks || []).filter((link) => Number(link.projectId) !== pid));
      return;
    }
    setLinks(
      (projectLinks || []).map((link) => (Number(link.projectId) === pid ? next : link))
    );
  };

  const setCoveredFootage = (id, rawValue) => {
    if (disabled) return;
    const pid = Number(id);
    const existing = linkById.get(pid);
    if (!existing) return;
    const trimmed = String(rawValue ?? "").trim();
    let coveredFootage = null;
    if (trimmed !== "") {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n <= 0) return;
      coveredFootage = n;
    }
    const next = { ...existing, coveredFootage };
    setLinks(
      (projectLinks || []).map((link) => (Number(link.projectId) === pid ? next : link))
    );
  };

  return (
    <div className="contracts-project-picker">
      {selectedFootage ? (
        <p
          className="contracts-muted"
          style={{ padding: "0.55rem 0.65rem", margin: 0, borderBottom: "1px solid #e2e8f0" }}
        >
          Selected footage: <strong>{selectedFootage}</strong>
        </p>
      ) : null}
      <div className="contracts-project-picker-search">
        <input
          type="search"
          placeholder="Search work order, name, customer…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
      </div>
      {filtered.length === 0 ? (
        <p className="contracts-muted" style={{ padding: "0.65rem" }}>
          No projects match.
        </p>
      ) : (
        filtered.map((row) => {
          const footageLine = formatProjectFootageLine(row);
          const link = linkById.get(row.id);
          const isSelected = Boolean(link);
          return (
            <div
              key={row.id}
              className={`contracts-project-row${isSelected ? " contracts-project-row--selected" : ""}`}
            >
              <label className="contracts-project-row-main">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleProject(row.id)}
                  disabled={disabled}
                />
                <span>
                  <strong>{row.work_order || `#${row.id}`}</strong> — {row.name || "Untitled"}
                  <div className="contracts-project-row-meta">
                    {projectTypeLabel(row)}
                    {row.customer ? ` · ${row.customer}` : ""}
                    {footageLine ? ` · ${footageLine}` : ""}
                  </div>
                </span>
              </label>
              {isSelected ? (
                <div className="contracts-project-roles" onClick={(e) => e.stopPropagation()}>
                  <label className="contracts-project-role">
                    <input
                      type="checkbox"
                      checked={Boolean(link.appliesConstruction)}
                      onChange={(e) => setRole(row.id, "construction", e.target.checked)}
                      disabled={disabled}
                    />
                    Construction
                  </label>
                  <label className="contracts-project-role">
                    <input
                      type="checkbox"
                      checked={Boolean(link.appliesFiber)}
                      onChange={(e) => setRole(row.id, "fiber", e.target.checked)}
                      disabled={disabled}
                    />
                    Fiber
                  </label>
                  <label className="contracts-project-coverage">
                    <span>Coverage ft</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="decimal"
                      className="contracts-project-coverage-input"
                      placeholder={defaultCoveragePlaceholder(row, link)}
                      value={link.coveredFootage != null ? link.coveredFootage : ""}
                      onChange={(e) => setCoveredFootage(row.id, e.target.value)}
                      disabled={disabled}
                      aria-label="Coverage footage"
                      title="Optional. Leave blank to use full project footage."
                    />
                  </label>
                </div>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}

function ContractsPageShell({ children }) {
  return (
    <div className="contracts-shell">
      <div className="contracts-page">{children}</div>
    </div>
  );
}

function ContractsGrid({ contracts, apiUrl, getAuthHeaders }) {
  const { agGridTheme } = useBoardTheme();
  const gridContext = useMemo(
    () => ({
      getAuthHeaders,
      fileUrl: (id) => contractExecutedFileUrl(id, apiUrl),
      permitZipUrl: (id) => contractPermitFilesZipUrl(id, apiUrl),
    }),
    [apiUrl, getAuthHeaders]
  );

  const columnDefs = useMemo(
    () => [
      {
        colId: "contractNumber",
        headerName: "ID",
        width: 90,
        valueGetter: (p) => p.data?.contractNumberLabel || p.data?.id,
        cellClass: "contracts-muted",
      },
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        minWidth: 180,
        cellRenderer: ContractTitleCell,
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        cellRenderer: ContractStatusCell,
      },
      {
        colId: "contractor",
        headerName: "Contractor",
        flex: 1,
        minWidth: 140,
        valueGetter: (p) => contractTableContractorLabel(p.data),
      },
      {
        colId: "effectiveContractCost",
        headerName: "Value",
        width: 120,
        valueGetter: (p) =>
          p.data?.effectiveContractCost != null ? p.data.effectiveContractCost : p.data?.contractCost,
        valueFormatter: (p) => formatContractCost(p.value),
        filter: "agNumberColumnFilter",
      },
      {
        field: "dropboxLink",
        headerName: "Dropbox",
        width: 100,
        sortable: false,
        filter: false,
        cellRenderer: ContractDropboxLinkCell,
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        width: 120,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : "—"),
      },
      {
        colId: "executedContract",
        headerName: "Contract",
        width: 110,
        sortable: false,
        filter: false,
        cellRenderer: ContractFileDownloadCell,
        cellRendererParams: { fileKey: "executedFile", label: "Download executed contract" },
      },
      {
        colId: "permitZip",
        headerName: "Permits ZIP",
        width: 110,
        sortable: false,
        filter: false,
        cellRenderer: ContractPermitZipCell,
      },
    ],
    []
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(id);
  }, [contracts.length]);

  if (!contracts.length) {
    return <p className="contracts-muted">No contracts yet. Create one to link projects.</p>;
  }

  return (
    <div className="contracts-grid-wrap">
      <AgGridReact
        theme={agGridTheme}
        rowData={contracts}
        columnDefs={columnDefs}
        context={gridContext}
        domLayout="autoHeight"
        getRowId={(p) => String(p.data?.id)}
        defaultColDef={{ sortable: true, filter: true, resizable: true }}
      />
    </div>
  );
}

function ContractAdjustmentsPanel({
  contract,
  canEdit,
  apiUrl,
  getAuthHeaders,
  disabled,
  onRefresh,
  onError,
}) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [supportFile, setSupportFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const adjustments = Array.isArray(contract?.adjustments) ? contract.adjustments : [];
  const adjustmentsTotal = Number(contract?.adjustmentsTotal) || 0;
  const baseCost = contract?.contractCost;
  const effectiveCost =
    contract?.effectiveContractCost != null
      ? contract.effectiveContractCost
      : baseCost != null
        ? baseCost + adjustmentsTotal
        : adjustmentsTotal !== 0
          ? adjustmentsTotal
          : null;

  const ctx = useMemo(() => ({ apiUrl, getAuthHeaders }), [apiUrl, getAuthHeaders]);

  const downloadSupportFile = async (adjustmentId, filename) => {
    try {
      const res = await axios.get(
        contractAdjustmentSupportFileUrl(contract.id, adjustmentId, apiUrl),
        { headers: getAuthHeaders?.(), responseType: "blob" }
      );
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "adjustment-support";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      onError?.(err?.response?.data?.error || err.message || "Could not download support file.");
    }
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!contract?.id || !canEdit) return;
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed === 0) {
      onError?.("Enter a non-zero amount. Use a negative number for credits.");
      return;
    }
    setBusy(true);
    onError?.("");
    try {
      let updated = await createContractAdjustment(
        contract.id,
        {
          amount: parsed,
          description: description.trim() || null,
        },
        ctx
      );
      if (supportFile) {
        const created = [...(updated.adjustments || [])].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )[0];
        if (created?.id) {
          updated = await uploadAdjustmentSupportFile(contract.id, created.id, supportFile, ctx);
        }
      }
      setAmount("");
      setDescription("");
      setSupportFile(null);
      await onRefresh?.(updated);
    } catch (err) {
      onError?.(err?.response?.data?.error || err.message || "Could not add adjustment.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (adjustmentId) => {
    if (!contract?.id || !canEdit) return;
    if (!window.confirm("Remove this cost adjustment?")) return;
    setBusy(true);
    onError?.("");
    try {
      const updated = await deleteContractAdjustment(contract.id, adjustmentId, ctx);
      await onRefresh?.(updated);
    } catch (err) {
      onError?.(err?.response?.data?.error || err.message || "Could not remove adjustment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="contracts-field">
      <label>Cost adjustments</label>
      <p className="contracts-muted" style={{ margin: "0 0 0.65rem", fontSize: "0.82rem" }}>
        Add positive amounts for extra scope or negative amounts when work is removed. Upload
        supporting documentation for each change. Update linked projects separately when scope
        changes.
      </p>
      <div className="contracts-adjustments-summary">
        <span>Base cost: {formatContractCost(baseCost)}</span>
        <span>Adjustments: {formatSignedContractCost(adjustmentsTotal)}</span>
        <strong>Effective total: {formatContractCost(effectiveCost)}</strong>
      </div>
      {adjustments.length ? (
        <ul className="contracts-adjustments-list">
          {adjustments.map((adj) => (
            <li key={adj.id} className="contracts-adjustments-item">
              <div className="contracts-adjustments-item-main">
                <span
                  className={`contracts-adjustments-amount${
                    Number(adj.amount) < 0 ? " contracts-adjustments-amount--credit" : ""
                  }`}
                >
                  {formatSignedContractCost(adj.amount)}
                </span>
                <span className="contracts-adjustments-desc">
                  {adj.description?.trim() || "Cost adjustment"}
                </span>
                <span className="contracts-muted contracts-adjustments-date">
                  {adj.createdAt ? new Date(adj.createdAt).toLocaleDateString() : ""}
                </span>
              </div>
              <div className="contracts-adjustments-item-actions">
                {adj.supportFile?.id ? (
                  <button
                    type="button"
                    className="contracts-btn"
                    onClick={() =>
                      downloadSupportFile(adj.id, adj.supportFile.filename || "support-file")
                    }
                    disabled={busy}
                  >
                    Download support
                  </button>
                ) : (
                  <span className="contracts-muted">No file</span>
                )}
                {canEdit ? (
                  <button
                    type="button"
                    className="contracts-btn contracts-btn--danger"
                    onClick={() => handleDelete(adj.id)}
                    disabled={busy || disabled}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="contracts-muted">No adjustments yet.</p>
      )}
      {canEdit ? (
        <div className="contracts-adjustments-form">
          <input
            type="number"
            step="0.01"
            placeholder="Amount (+ or −)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                handleAdd();
              }
            }}
            disabled={busy || disabled}
            aria-label="Adjustment amount"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                handleAdd();
              }
            }}
            disabled={busy || disabled}
            aria-label="Adjustment description"
          />
          <label className="contracts-btn" style={{ cursor: "pointer", margin: 0 }}>
            {supportFile ? supportFile.name : "Support file"}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              style={{ display: "none" }}
              disabled={busy || disabled}
              onChange={(e) => setSupportFile(e.target.files?.[0] || null)}
            />
          </label>
          <button
            type="button"
            className="contracts-btn contracts-btn--primary"
            disabled={busy || disabled}
            onClick={handleAdd}
          >
            {busy ? "Saving…" : "Add adjustment"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ContractorSelect({ id, label, value, onChange, options, disabled, hint }) {
  const listId = `${id}-options`;
  return (
    <div className="contracts-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Select or type contractor name"
      />
      <datalist id={listId}>
        {options.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {hint ? (
        <p className="contracts-muted" style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function ContractEditor({
  contract,
  projects,
  contractorOptions,
  apiUrl,
  getAuthHeaders,
  canEdit,
  saving,
  onSave,
  onDelete,
  onCancel,
  onContractRefresh,
}) {
  const [draft, setDraft] = useState(() => contractToDraft(contract));
  const [error, setError] = useState("");
  const [uploadingExecuted, setUploadingExecuted] = useState(false);
  const [uploadingWinningBid, setUploadingWinningBid] = useState(false);

  useEffect(() => {
    setDraft(contractToDraft(contract));
    setError("");
  }, [contract]);

  const linkedProjects = useMemo(() => {
    const map = new Map(projects.map((p) => [p.id, p]));
    const contractProjectMap = new Map((contract?.projects || []).map((p) => [p.id, p]));
    return (draft.projectLinks || [])
      .map((link) => {
        const pid = Number(link.projectId);
        const fromList = map.get(pid);
        if (fromList) return { ...fromList, ...link, id: pid };
        const fromContract = contractProjectMap.get(pid);
        if (!fromContract) return null;
        return {
          id: pid,
          work_order: fromContract.workOrder || "",
          name: fromContract.name || "",
          ...link,
        };
      })
      .filter(Boolean);
  }, [draft.projectLinks, projects, contract?.projects]);

  const isAwarded = draft.status === "awarded";
  const canDownloadTemplate =
    Boolean(contract?.id) &&
    (draft.status === "pending_award" || draft.status === "awarded");
  const ctx = useMemo(() => ({ apiUrl, getAuthHeaders }), [apiUrl, getAuthHeaders]);

  const setDraftField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await onSave(draft);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Save failed.");
    }
  };

  const downloadTemplate = async () => {
    if (!canDownloadTemplate) return;
    try {
      const res = await axios.get(contractTemplateDownloadUrl(contract.id, apiUrl), {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-${contract.contractNumberLabel || contract.id}-template.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const data = err?.response?.data;
      let msg = "Could not download template.";
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
      } else if (typeof data?.error === "string") {
        msg = data.error;
      } else if (err?.message) {
        msg = err.message;
      }
      setError(msg);
    }
  };

  const onExecutedFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !contract?.id) return;
    setUploadingExecuted(true);
    setError("");
    try {
      await uploadExecutedContractFile(contract.id, file, ctx);
      await onContractRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Upload failed.");
    } finally {
      setUploadingExecuted(false);
      e.target.value = "";
    }
  };

  const onWinningBidFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !contract?.id) return;
    setUploadingWinningBid(true);
    setError("");
    try {
      await uploadWinningBidFile(contract.id, file, ctx);
      await onContractRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Upload failed.");
    } finally {
      setUploadingWinningBid(false);
      e.target.value = "";
    }
  };

  const downloadContractFile = async (url, filename, errorMessage) => {
    try {
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const data = err?.response?.data;
      let msg = errorMessage;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
      } else if (typeof data?.error === "string") {
        msg = data.error;
      } else if (err?.message) {
        msg = err.message;
      }
      setError(msg);
    }
  };

  return (
    <form className="contracts-panel" onSubmit={handleSubmit}>
      <h2 className="contracts-panel-title">
        {contract?.id ? (
          <>
            Contract #{contract.contractNumberLabel || contract.id}
            <span className="contracts-muted" style={{ display: "block", fontSize: "0.9rem", fontWeight: 400 }}>
              {contract.title}
            </span>
          </>
        ) : (
          "New contract"
        )}
      </h2>
      {error ? <p className="contracts-error">{error}</p> : null}
      <div className="contracts-form-grid">
        <div className="contracts-field">
          <label htmlFor="contract-number">Contract ID</label>
          <input
            id="contract-number"
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            pattern="[A-Za-z0-9][A-Za-z0-9_-]*"
            title="Letters, numbers, hyphen, or underscore (e.g. 1001a)"
            value={draft.contractNumber}
            onChange={(e) => setDraftField("contractNumber", e.target.value)}
            disabled={!canEdit || saving}
            placeholder={contract?.id ? "Contract ID" : "Auto-assigned if blank"}
            required={Boolean(contract?.id)}
          />
          <p className="contracts-muted" style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
            Alphanumeric IDs allowed (e.g. 1001a). Must be unique.
          </p>
        </div>
        <div className="contracts-field">
          <label htmlFor="contract-title">Title</label>
          <input
            id="contract-title"
            value={draft.title}
            onChange={(e) => setDraftField("title", e.target.value)}
            required
            disabled={!canEdit || saving}
            placeholder="e.g. SEG 42 conduit package"
          />
        </div>
        <div className="contracts-field">
          <label htmlFor="contract-status">Status</label>
          <select
            id="contract-status"
            value={draft.status}
            onChange={(e) => setDraftField("status", e.target.value)}
            disabled={!canEdit || saving}
          >
            {CONTRACT_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="contracts-field">
          <label htmlFor="contract-cost">Contract cost</label>
          <input
            id="contract-cost"
            type="number"
            min="0"
            step="0.01"
            value={draft.contractCost}
            onChange={(e) => setDraftField("contractCost", e.target.value)}
            disabled={!canEdit || saving}
          />
          {contract?.id && Array.isArray(contract.adjustments) && contract.adjustments.length ? (
            <p className="contracts-muted" style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
              Effective total after adjustments:{" "}
              <strong>{formatContractCost(contract.effectiveContractCost)}</strong>
            </p>
          ) : null}
        </div>
        {contract?.id ? (
          <ContractAdjustmentsPanel
            contract={contract}
            canEdit={canEdit}
            apiUrl={apiUrl}
            getAuthHeaders={getAuthHeaders}
            disabled={saving || uploadingExecuted || uploadingWinningBid}
            onRefresh={onContractRefresh}
            onError={setError}
          />
        ) : null}
        <div className="contracts-field">
          <label htmlFor="contract-execution-date">Contract execution date</label>
          <input
            id="contract-execution-date"
            type="date"
            value={draft.contractExecutionDate || ""}
            onChange={(e) => setDraftField("contractExecutionDate", e.target.value)}
            disabled={!canEdit || saving}
          />
        </div>
        <div className="contracts-field">
          <label htmlFor="contract-expiration-date">Contract expiration date</label>
          <input
            id="contract-expiration-date"
            type="date"
            value={draft.contractExpirationDate || ""}
            onChange={(e) => setDraftField("contractExpirationDate", e.target.value)}
            disabled={!canEdit || saving}
          />
        </div>
        <ContractorSelect
          id="contractor"
          label="Construction contractor"
          value={draft.contractor}
          onChange={(v) => setDraftField("contractor", v)}
          options={contractorOptions}
          disabled={!canEdit || saving || !isAwarded}
          hint={
            isAwarded
              ? "Select the awarded construction contractor."
              : "Set status to Awarded to select a contractor."
          }
        />
        <ContractorSelect
          id="fiber-contractor"
          label="Fiber contractor"
          value={draft.fiberContractor}
          onChange={(v) => setDraftField("fiberContractor", v)}
          options={contractorOptions}
          disabled={!canEdit || saving || !isAwarded}
          hint={
            isAwarded
              ? "Select the awarded fiber contractor when applicable."
              : "Set status to Awarded to select a fiber contractor."
          }
        />
        <div className="contracts-field">
          <label htmlFor="contract-dropbox-link">Dropbox link</label>
          <input
            id="contract-dropbox-link"
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            value={draft.dropboxLink}
            onChange={(e) => setDraftField("dropboxLink", e.target.value)}
            disabled={!canEdit || saving}
            placeholder="https://www.dropbox.com/…"
          />
        </div>
        <div className="contracts-field">
          <label htmlFor="contract-notes">Notes</label>
          <textarea
            id="contract-notes"
            value={draft.notes}
            onChange={(e) => setDraftField("notes", e.target.value)}
            disabled={!canEdit || saving}
            placeholder="Optional details"
          />
        </div>
        {contract?.id ? (
          <div className="contracts-field">
            <label>Contract documents</label>
            <div className="contracts-actions-row" style={{ marginTop: 0 }}>
              {canDownloadTemplate ? (
                <button type="button" className="contracts-btn" onClick={downloadTemplate} disabled={saving}>
                  Download template PDF
                </button>
              ) : null}
              {(contract.projectIds?.length || contract.projects?.length) ? (
                <button
                  type="button"
                  className="contracts-btn"
                  onClick={() =>
                    downloadContractFile(
                      contractPermitFilesZipUrl(contract.id, apiUrl),
                      `contract-${contract.contractNumberLabel || contract.id}-permit-files.zip`,
                      "Could not download permit files."
                    )
                  }
                  disabled={saving}
                >
                  Download permits ZIP
                </button>
              ) : null}
              {contract.winningBidFile ? (
                <button
                  type="button"
                  className="contracts-btn"
                  onClick={() =>
                    downloadContractFile(
                      contractWinningBidFileUrl(contract.id, apiUrl),
                      contract.winningBidFile.filename || "winning-bid",
                      "Could not download winning bid."
                    )
                  }
                >
                  Download winning bid
                </button>
              ) : null}
              {canEdit ? (
                <label className="contracts-btn" style={{ cursor: "pointer" }}>
                  {uploadingWinningBid ? "Uploading…" : "Upload winning bid PDF"}
                  <input
                    type="file"
                    accept=".pdf"
                    style={{ display: "none" }}
                    disabled={uploadingWinningBid || uploadingExecuted || saving}
                    onChange={onWinningBidFileChange}
                  />
                </label>
              ) : null}
              {contract.executedFile ? (
                <button
                  type="button"
                  className="contracts-btn"
                  onClick={() =>
                    downloadContractFile(
                      contractExecutedFileUrl(contract.id, apiUrl),
                      contract.executedFile.filename || "executed-contract",
                      "Could not download executed contract."
                    )
                  }
                >
                  Download executed contract
                </button>
              ) : null}
              {canEdit ? (
                <label className="contracts-btn" style={{ cursor: "pointer" }}>
                  {uploadingExecuted ? "Uploading…" : "Upload executed contract"}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    disabled={uploadingExecuted || uploadingWinningBid || saving}
                    onChange={onExecutedFileChange}
                  />
                </label>
              ) : null}
            </div>
            {contract.winningBidFile || contract.executedFile ? (
              <div className="contracts-muted" style={{ marginTop: "0.5rem" }}>
                {contract.winningBidFile ? (
                  <p style={{ margin: "0 0 0.25rem" }}>
                    Winning bid: {contract.winningBidFile.filename}
                  </p>
                ) : null}
                {contract.executedFile ? (
                  <p style={{ margin: 0 }}>Executed contract: {contract.executedFile.filename}</p>
                ) : null}
              </div>
            ) : (
              <p className="contracts-muted" style={{ marginTop: "0.5rem" }}>
                Upload the winning bid PDF when available. Download the template and upload the signed
                PDF once executed.
              </p>
            )}
          </div>
        ) : null}
        <div className="contracts-field">
          <label>Linked projects</label>
          <p className="contracts-muted" style={{ margin: "0 0 0.5rem", fontSize: "0.82rem" }}>
            Select work orders, then choose whether this contract covers construction, fiber, or both
            for each one. Coverage footage is optional — leave blank to use the full project footage.
          </p>
          {linkedProjects.length ? (
            <div className="contracts-chip-list" style={{ marginBottom: "0.5rem" }}>
              {linkedProjects.map((p) => {
                const roles = roleLabelsForLink(p);
                return (
                  <span key={p.id} className="contracts-chip">
                    <Link to={`/project/${p.id}`} className="contracts-link">
                      {projectLinkLabel(p)}
                    </Link>
                    {roles.length ? (
                      <span className="contracts-chip-roles">{roles.join(" · ")}</span>
                    ) : null}
                    {canEdit ? (
                      <button
                        type="button"
                        aria-label="Remove project"
                        onClick={() =>
                          setDraftField(
                            "projectLinks",
                            (draft.projectLinks || []).filter((link) => Number(link.projectId) !== p.id)
                          )
                        }
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                );
              })}
            </div>
          ) : (
            <p className="contracts-muted">No projects linked yet.</p>
          )}
          {canEdit ? (
            <ProjectPicker
              projects={projects}
              projectLinks={draft.projectLinks || []}
              onChange={(links) => setDraftField("projectLinks", links)}
              disabled={saving}
            />
          ) : null}
        </div>
      </div>
      <div className="contracts-actions-row">
        {canEdit ? (
          <button type="submit" className="contracts-btn contracts-btn--primary" disabled={saving || uploadingExecuted || uploadingWinningBid}>
            {saving ? "Saving…" : "Save"}
          </button>
        ) : null}
        {onCancel ? (
          <button type="button" className="contracts-btn" onClick={onCancel} disabled={saving || uploadingExecuted || uploadingWinningBid}>
            {canEdit ? "Cancel" : "Back"}
          </button>
        ) : null}
        {canEdit && contract?.id && onDelete ? (
          <button
            type="button"
            className="contracts-btn contracts-btn--danger"
            disabled={saving || uploadingExecuted || uploadingWinningBid}
            onClick={onDelete}
          >
            Delete
          </button>
        ) : null}
      </div>
    </form>
  );
}

function ContractsPageInner({ apiUrl, getAuthHeaders }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const contractId = isNew ? null : id;

  const canEdit = canManageContracts();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [contracts, setContracts] = useState([]);
  const [contract, setContract] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contractorOptions, setContractorOptions] = useState([]);

  const ctx = useMemo(() => ({ apiUrl, getAuthHeaders }), [apiUrl, getAuthHeaders]);

  const loadProjects = useCallback(async () => {
    const url = apiUrl || getApiBase();
    const { data } = await axios.get(`${url}/projects`, { headers: getAuthHeaders() });
    setProjects(Array.isArray(data) ? data : []);
  }, [apiUrl, getAuthHeaders]);

  const loadList = useCallback(async () => {
    const rows = await fetchContracts(ctx);
    setContracts(rows);
  }, [ctx]);

  const loadContract = useCallback(async () => {
    if (!contractId) return;
    const row = await fetchContract(contractId, ctx);
    setContract(row);
  }, [contractId, ctx]);

  const loadContractorOptions = useCallback(async () => {
    const rows = await fetchContractorOptions(ctx);
    setContractorOptions(rows);
  }, [ctx]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        await Promise.all([loadProjects(), loadContractorOptions()]);
        if (contractId || isNew) {
          if (!isNew) await loadContract();
          else setContract(emptyContractDraft());
        } else {
          await loadList();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err.message || "Could not load contracts.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId, isNew, loadContract, loadList, loadContractorOptions, loadProjects]);

  const buildPayload = (draft) => ({
    contractNumber: draft.contractNumber === "" ? null : String(draft.contractNumber).trim(),
    title: draft.title,
    status: draft.status,
    notes: draft.notes,
    contractor: draft.contractor,
    fiberContractor: draft.fiberContractor,
    contractCost: draft.contractCost === "" ? null : Number(draft.contractCost),
    contractExecutionDate: draft.contractExecutionDate || null,
    contractExpirationDate: draft.contractExpirationDate || null,
    projectLinks: (draft.projectLinks || []).map((link) => ({
      projectId: Number(link.projectId),
      appliesConstruction: link.appliesConstruction !== false,
      appliesFiber: link.appliesFiber !== false,
      coveredFootage: normalizeCoveredFootageValue(link.coveredFootage),
    })),
    dropboxLink: draft.dropboxLink === "" ? null : String(draft.dropboxLink).trim(),
  });

  const handleSave = async (draft) => {
    setSaving(true);
    try {
      const payload = buildPayload(draft);
      if (isNew || !contract?.id) {
        const created = await createContract(payload, ctx);
        navigate(`/contracts/${created.id}`, { replace: true });
        return;
      }
      await updateContract(contract.id, payload, ctx);
      await setContractProjects(contract.id, { projectLinks: payload.projectLinks }, ctx);
      await loadContract();
      await loadList();
      await loadContractorOptions();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contract?.id) return;
    if (!window.confirm(`Delete contract "${contract.title}"?`)) return;
    setSaving(true);
    try {
      await deleteContract(contract.id, ctx);
      navigate("/contracts");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Delete failed.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ContractsPageShell>
        <p className="contracts-muted">Loading contracts…</p>
      </ContractsPageShell>
    );
  }

  if (contractId || isNew) {
    return (
      <ContractsPageShell>
        <Link to="/contracts" className="project-detail-back project-landing-back">
          ← All contracts
        </Link>
        {error ? <p className="contracts-error">{error}</p> : null}
        <ContractEditor
          contract={contract}
          projects={projects}
          contractorOptions={contractorOptions}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          canEdit={canEdit}
          saving={saving}
          onSave={handleSave}
          onDelete={handleDelete}
          onCancel={() => navigate("/contracts")}
          onContractRefresh={async (updated) => {
            if (updated) setContract(updated);
            else await loadContract();
            await loadList();
          }}
        />
      </ContractsPageShell>
    );
  }

  const awardedCount = contracts.filter((c) => c.status === "awarded").length;
  const pendingCount = contracts.filter((c) => c.status === "pending_award").length;
  const cancelledCount = contracts.filter((c) => c.status === "cancelled").length;

  return (
    <ContractsPageShell>
      <div className="contracts-page-header">
        <div>
          <h1 className="contracts-page-title">Contracts</h1>
          <p className="contracts-page-lead">
            Link commercial and expansion projects to contracts. Subsegment construction fields sync
            from the linked contract.
          </p>
          <p className="contracts-muted">
            {awardedCount} awarded · {pendingCount} pending award
            {cancelledCount > 0 ? ` · ${cancelledCount} cancelled` : ""}
          </p>
        </div>
        {canEdit ? (
          <Link to="/contracts/new" className="contracts-btn contracts-btn--primary">
            New contract
          </Link>
        ) : null}
      </div>
      {error ? <p className="contracts-error">{error}</p> : null}
      <section className="contracts-panel">
        <ContractsGrid contracts={contracts} apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />
      </section>
    </ContractsPageShell>
  );
}

export function ContractsGate({ apiUrl, getAuthHeaders }) {
  if (!isPluginEnabled(PLUGIN_IDS.CONTRACTS)) {
    return <Navigate to="/" replace />;
  }
  if (!canViewContracts()) {
    return <Navigate to="/" replace />;
  }
  return <ContractsPageInner apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}

export default ContractsGate;
